import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from 'recharts';
import { Card, SegmentedControl } from '../../../ui';
import { RANGES, fmtCount, fmtMoney, type JournalEntry, type KpiRow, type Range } from '../../../../lib/googleAds';
import useMediaQuery, { PHONE_QUERY } from '../../../clients/useMediaQuery';

type Metric = 'cpa' | 'leads' | 'cost';

const METRICS: { id: Metric; label: string }[] = [
  { id: 'cpa', label: 'CPA' },
  { id: 'leads', label: 'Leads' },
  { id: 'cost', label: 'Spend' },
];


const axisTick = { fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'var(--font-mono)' };

const fmtDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * The trailing-30-day line, one point per logged day. Tracking changes from the journal are drawn as markers:
 * the numbers either side of one count different things, so the line is not comparable across it.
 */
interface AdsKpiChartProps {
  rows: KpiRow[];
  journal: JournalEntry[];
  /** The time frame is the section's: the KPI cards above follow it too. */
  range: Range;
  onRangeChange: (range: Range) => void;
}

export default function AdsKpiChart({ rows, journal, range, onRangeChange }: AdsKpiChartProps) {
  const [metric, setMetric] = useState<Metric>('cpa');
  // Two segmented controls (eight options) can't sit beside the title on a phone: they ran 150px past the edge.
  // There they get their own wrapping row above the chart.
  const phone = useMediaQuery(PHONE_QUERY);

  // The last N days up to the newest row (not up to today, so a stalled sync still shows its last stretch).
  const shown = useMemo(() => {
    if (range === 'all' || rows.length === 0) return rows;
    const [y, m, d] = rows[rows.length - 1].snapshot_date.split('-').map(Number);
    const from = new Date(Date.UTC(y, m - 1, d - Number(range) + 1)).toISOString().slice(0, 10);
    return rows.filter((r) => r.snapshot_date >= from);
  }, [rows, range]);

  const points = useMemo(() => shown.map((r) => ({ ...r, label: fmtDay(r.snapshot_date) })), [shown]);
  const markers = useMemo(() => {
    if (shown.length === 0) return [];
    const first = shown[0].snapshot_date;
    const last = shown[shown.length - 1].snapshot_date;
    return journal
      .filter((e) => e.kind === 'tracking_change' && e.entry_date >= first && e.entry_date <= last)
      .map((e) => ({ label: fmtDay(e.entry_date), title: e.title }));
  }, [shown, journal]);

  const fmt = (v: number | null) => (metric === 'leads' ? fmtCount(v) : fmtMoney(v, metric === 'cpa' ? 2 : 0));

  const KpiTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as KpiRow & { label: string };
    const marker = markers.find((m) => m.label === p.label);
    return (
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-pop)',
          maxWidth: 280,
        }}
      >
        <p className="t-eyebrow" style={{ margin: 0 }}>30 days to {p.label}</p>
        <p className="t-num" style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-body)', lineHeight: 1.6 }}>
          CPA {fmtMoney(p.cpa, 2)} · {fmtCount(p.leads)} leads · {fmtMoney(p.cost)} spend
          <br />
          <span style={{ color: 'var(--text-muted)' }}>
            {fmtCount(p.clicks)} clicks · {p.source === 'backfill' ? 'backfilled' : 'logged that morning'}
          </span>
        </p>
        {marker ? (
          <p style={{ margin: '6px 0 0', fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>Tracking change: {marker.title}</p>
        ) : null}
      </div>
    );
  };

  const controls = (
    <>
      <SegmentedControl options={RANGES} value={range} onChange={(id) => onRangeChange(id as Range)} />
      <SegmentedControl options={METRICS} value={metric} onChange={(id) => setMetric(id as Metric)} />
    </>
  );

  return (
    <Card
      eyebrow="Trailing 30 days"
      title={METRICS.find((m) => m.id === metric)?.label}
      actions={phone ? undefined : controls}
    >
      {phone ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>{controls}</div>
      ) : null}
      {points.length === 0 ? (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>
          Nothing logged yet. The first sync fills the account's whole history.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={points} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={axisTick}
              axisLine={{ stroke: 'var(--border-hairline)' }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={48}
              height={24}
            />
            <YAxis
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              width={52}
              domain={[0, 'auto']}
              allowDecimals={metric !== 'leads'}
              tickFormatter={(v: number) => (metric === 'leads' ? String(v) : `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v)}`)}
            />
            <Tooltip content={KpiTooltip} />
            {markers.map((m) => (
              <ReferenceLine key={`${m.label}-${m.title}`} x={m.label} stroke="var(--status-down)" strokeDasharray="4 3" />
            ))}
            <Line
              type="monotone"
              dataKey={metric}
              stroke="var(--chart-primary)"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      {points.length > 0 ? (
        <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
          Logged every morning for the 30 days to yesterday. {fmt(points[points.length - 1][metric])} on the latest read.
          {markers.length ? ' Red dashes are tracking changes: don’t compare across them.' : ''}
        </p>
      ) : null}
    </Card>
  );
}
